import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Copy, Check, Webhook } from 'lucide-react';
import { toast } from 'sonner';

interface TrackingWebhook {
  name: string; // nome da intro
  url: string;  // URL de webhook gerada pela API
}

interface TrackingWebhooksResultModalProps {
  open: boolean;
  webhooks: TrackingWebhook[];
  platformName?: string;
  onClose: () => void;
}

/**
 * Exibido após criar um teste com rastreamento: lista as URLs de webhook geradas
 * (uma por intro) para o usuário copiar e colar no painel da plataforma de
 * pagamento. Sem essa configuração na plataforma, nenhuma conversão é recebida.
 */
export function TrackingWebhooksResultModal({ open, webhooks, platformName, onClose }: TrackingWebhooksResultModalProps) {
  useLanguage();
  const [copiedIndex, setCopiedIndex] = React.useState<number | null>(null);

  const handleCopy = (url: string, index: number) => {
    navigator.clipboard.writeText(url);
    setCopiedIndex(index);
    toast.success(translateInterfaceText("URL copiada"), { position: 'top-center' });
    setTimeout(() => setCopiedIndex((curr) => (curr === index ? null : curr)), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-[560px]">
        <DialogHeader>
          <div className="mb-1 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#EEF2FF] text-[#2563EB]">
              <Webhook className="h-[18px] w-[18px]" />
            </div>
            <DialogTitle className="text-[16px] font-semibold text-[#1A1A1A]">
              {translateInterfaceText("Rastreamento configurado")}</DialogTitle>
          </div>
          <DialogDescription className="text-[13px] leading-relaxed text-[#4B5563]">
            {translateInterfaceText("Cole a URL de webhook abaixo no painel")}{" "}{platformName ? translateInterfaceText("da {value0}", { value0: (platformName) }) : translateInterfaceText("da sua plataforma")} {" "}{translateInterfaceText("para cada intro. Sem isso, a plataforma não envia as conversões. Você também encontra estas URLs em")}{' '}
            <span className="font-medium text-[#1A1A1A]">{translateInterfaceText("Rastreamento")}</span> {" "}{translateInterfaceText("a qualquer momento.")}</DialogDescription>
        </DialogHeader>

        <div className="max-h-[320px] space-y-2.5 overflow-y-auto py-1">
          {webhooks.map((webhook, index) => (
            <div key={index} className="rounded-[10px] border border-[#E7EAF2] bg-[#FAFBFF] p-3">
              <p className="mb-1.5 truncate text-[13px] font-medium text-[#2C3345]">{webhook.name}</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 truncate rounded-md border border-[#E7EAF2] bg-white px-2.5 py-1.5 text-[12px] text-[#6B7280]" title={webhook.url}>
                  {webhook.url}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 shrink-0 gap-1.5 rounded-md px-2.5"
                  onClick={() => handleCopy(webhook.url, index)}
                >
                  {copiedIndex === index ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                  <span className="text-[12px]">{copiedIndex === index ? translateInterfaceText("Copiado") : translateInterfaceText("Copiar")}</span>
                </Button>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button
            onClick={onClose}
            className="h-10 rounded-[10px] bg-[#1D4FFF] px-5 font-semibold text-white hover:bg-[#1A47E5]"
          >
            {translateInterfaceText("Concluir")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
